/**
 * API-валідація pricing-сценаріїв проти реального бекенда.
 * Винесено з pricingScenarios.js, щоб дані сценаріїв лишались чистим модулем
 * (без axios) і могли імпортуватись у Playwright-тести без браузера.
 */
import { calculateBundlePrice } from '../services/api';
import {
  PRICING_SCENARIOS,
  scenarioToBundleRequest,
  round2,
} from './pricingScenarios';

/**
 * Validate a scenario against the REAL backend API (not circular JS formulas)
 * This is the correct way to validate - calling the actual pricing engine
 */
export async function validateScenarioWithAPI(scenario) {
  try {
    // Allow scenario to explicitly skip API validation
    // (e.g. voice-traffic scenarios where backend price is hard to predict in JS)
    if (scenario.expected?.skipApiValidation) {
      return {
        passed: true,
        actual: null,
        expected: scenario.expected.basePrice,
        details: {},
        skipped: true,
        skipReason: 'Scenario marked skipApiValidation',
      };
    }

    const bundleRequest = scenarioToBundleRequest(scenario);

    // Skip if no products (e.g., FMC for non-UA regions, phone-only scenarios)
    if (bundleRequest.products.length === 0) {
      return {
        passed: true,
        actual: 0,
        expected: 0,
        details: {},
        skipped: true,
        skipReason: 'No backend products for this region',
      };
    }

    const response = await calculateBundlePrice(bundleRequest);
    const bundleResult = response.data;

    // Extract total price from API response
    const actualPrice = bundleResult.total?.primary?.amount || 0;
    // Compare backend API against backendBasePrice when available
    // (handles scenarios where UI total includes frontend-only items like phone numbers)
    const expectedBackendPrice = scenario.expected.backendBasePrice ?? scenario.expected.basePrice;

    // Compare with tolerance (2 cents)
    const passed = Math.abs(actualPrice - expectedBackendPrice) < 0.02;

    // Extract individual product prices for debugging
    const details = {};
    bundleResult.products?.forEach(product => {
      details[product.product_id] = {
        base_price: product.base_price,
        final_price: product.final_price,
        bundle_discount: product.bundle_discount || 0,
      };
    });

    return {
      passed,
      actual: round2(actualPrice),
      expected: expectedBackendPrice,
      displayExpected: scenario.expected.basePrice,
      details,
      apiResponse: bundleResult,
    };
  } catch (error) {
    console.error('API validation error for scenario:', scenario.id, error);
    return {
      passed: false,
      actual: null,
      expected: scenario.expected.basePrice,
      error: error.message || 'API call failed',
      details: {},
    };
  }
}

/**
 * Process scenarios in batches to avoid rate limiting (429 errors)
 * @param {Array} scenarios - Array of scenarios to process
 * @param {number} batchSize - Number of concurrent requests
 * @param {number} delayMs - Delay between batches in milliseconds
 * @param {Function} onProgress - Progress callback (processed, total, partialResults)
 */
async function processScenariosInBatches(scenarios, batchSize = 5, delayMs = 200, onProgress = null) {
  const results = [];

  for (let i = 0; i < scenarios.length; i += batchSize) {
    const batch = scenarios.slice(i, i + batchSize);

    // Process batch in parallel
    const batchResults = await Promise.all(
      batch.map(async (scenario) => {
        const validation = await validateScenarioWithAPI(scenario);
        return {
          ...scenario,
          validation,
        };
      })
    );

    results.push(...batchResults);

    // Report progress with partial results for incremental UI update
    if (onProgress) {
      onProgress(results.length, scenarios.length, [...results]);
    }

    // Delay before next batch (except for last batch)
    if (i + batchSize < scenarios.length) {
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }

  return results;
}

/**
 * Get all scenarios with validation results from BACKEND API
 * This calls the real pricing engine for validation
 * Uses batching to avoid rate limiting
 * @param {Function} onProgress - Optional progress callback (processed, total, partialResults)
 */
export async function getAllScenariosWithResultsAsync(onProgress = null) {
  return processScenariosInBatches(
    PRICING_SCENARIOS,
    5,    // 5 concurrent requests per batch
    200,  // 200ms delay between batches
    onProgress
  );
}
